import React, { useState, useEffect } from 'react';
import { Form, Select, Button, Modal, message } from 'antd';
import FormCard from 'pages/NewUniProfile/components/FormCard';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import LicenseImg from 'components/LicenseImg';
import commonS from '../../s.module.less';
import s from './s.module.less';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields, SettingEnum } from 'types/enumerationData';
import { FormIdentify } from 'types/form';
import { editIdentification } from 'api/newUniprofile';
import { isMobile } from 'utils/common';

interface Props {
    initValue?: FormIdentify | null;
    onSave?: () => void;
    isWarning?: boolean;
}

const PersonalIndentification: React.FC<Props> = (props) => {
    const { initValue, onSave, isWarning = false } = props;
    const [idTypeOptions] = useGetSettingData(EnumFields.IDENTITY_TYPE)
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentImage, setCurrentImage] = useState<string | null>(null);
    const [fullUrls, setFullUrls] = useState<{
        fullFrontUrl?: string;
        fullBackUrl?: string;
    }>({});


    useEffect(() => {
        if (initValue) {
            form.setFieldsValue(initValue);
            setFullUrls({
                fullFrontUrl: initValue.fullFrontUrl,
                fullBackUrl: initValue.fullBackUrl,
            });
        }
    }, [initValue, form]);

    const handleFullUrlChange = (name: string) => (fullUrl: string) => {
        setFullUrls((prev) => ({ ...prev, [name]: fullUrl }));
    };

    const handleViewIdClick = (imgUrl: string) => {
        if (!imgUrl) return;
        setCurrentImage(imgUrl);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setCurrentImage(null);
    };

    const handleSave = async () => {
        try {
            const formValues = await form.validateFields();
            const result = await editIdentification({
                identification: formValues,
            });
            if (result.error) {
                message.error(result.error);
            } else {
                message.success('Personal identification has been updated');
                await onSave?.();
            }
        } catch (e: any) {
            if (e.errorFields) {
                //form error
                return;
            }
            const msg = e?.toString?.() || 'data error';
            message.error(msg);
        }
    };

    const renderEditing = () => {
        return (
            <Form
                form={form}
                layout="vertical"
                className={s.form}
            >
                <div className={commonS.row}>
                    <Form.Item
                        label="ID type"
                        name="type"
                        rules={[{ required: true, message: 'ID type is a required field' }]}
                        style={{ width: '360px' }}
                    >
                        <Select
                            fieldNames={{ label: 'content', value: 'dictKey' }}
                            placeholder="Choose a government-issued ID type"
                            options={idTypeOptions as SettingEnum[]}
                        />
                    </Form.Item>
                </div>

                <div className={commonS.row} style={{ flexDirection: 'column', gap: '8px' }}>
                    <div className={s.uploadLabel}>
                        Uploaded the government-issued ID
                    </div>
                    <div className='flex gap-4'>
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const type = getFieldValue('type');
                                if (type === 'passport') {
                                    return (
                                        <div className={s.row}>
                                            <Form.Item
                                                style={{ width: '334px' }}
                                                name={['frontUrl']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Copy of your government-issued ID is a required field',
                                                    },
                                                ]}
                                            >
                                                <FileUploadPrivate
                                                    onChangeFullUrl={handleFullUrlChange(
                                                        'fullFrontUrl',
                                                    )}
                                                    title={<span className={s.required}>Copy of your government-issued ID</span>}
                                                    previewMode="horizontal"
                                                />
                                            </Form.Item>
                                        </div>
                                    );
                                } else {
                                    return (
                                        <div className={commonS.row} style={{ flexDirection: isMobile() ? 'column' : 'row' }}>
                                            <Form.Item
                                                style={{ width: '334px' }}
                                                name={['frontUrl']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Front of your government-issued ID is a required field',
                                                    },
                                                ]}
                                            >
                                                <FileUploadPrivate
                                                    onChangeFullUrl={handleFullUrlChange(
                                                        'fullFrontUrl',
                                                    )}
                                                    title={<span className={s.required}>Front of your government-issued ID</span>}
                                                    previewMode="horizontal"
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                style={{ width: '334px' }}
                                                name={['backUrl']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Back of your government-issued ID is a required field',
                                                    },
                                                ]}
                                            >
                                                <FileUploadPrivate
                                                    onChangeFullUrl={handleFullUrlChange(
                                                        'fullBackUrl',
                                                    )}
                                                    title={<span className={s.required}>Back of your government-issued ID</span>}
                                                    previewMode="horizontal"
                                                />
                                            </Form.Item>
                                        </div>
                                    );
                                }
                            }}
                        </Form.Item>
                    </div>
                </div>
            </Form>
        );
    };

    const renderReview = () => {
        const hasData = initValue && initValue.frontUrl;
        const { type, fullFrontUrl, fullBackUrl } = initValue || {};
        const idTypeLabel = (idTypeOptions as SettingEnum[])?.find(
            (item) => item.dictKey === type,
        )?.content;
        const isPassport = type === 'passport';

        return (
            <div>
                <div className={commonS.row}>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>ID type</div>
                        <div className={commonS.value}>{idTypeLabel || '-'}</div>
                    </div>
                </div>

                <div className={commonS.row}>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label} style={{ marginBottom: hasData ? '16px' : '4px' }}>
                            Uploaded the government-issued ID
                        </div>
                        {hasData ? (
                            <div className={s.idImagesContainer}>
                                <div className={s.idImageItem}>
                                    <LicenseImg src={fullFrontUrl} />
                                    <Button
                                        className={s.viewId}
                                        type="link"
                                        onClick={() => handleViewIdClick(fullFrontUrl || '')}
                                    >
                                        View ID
                                    </Button>
                                </div>

                                {!isPassport && fullBackUrl && (
                                    <div className={s.idImageItem}>
                                        <LicenseImg src={fullBackUrl} />
                                        <Button
                                            className={s.viewId}
                                            type="link"
                                            onClick={() => handleViewIdClick(fullBackUrl || '')}
                                        >
                                            View ID
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className={commonS.value}>-</div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <FormCard type='review' title='Personal identification' hasContent={true} onSave={handleSave} isWarning={isWarning}>
                {(isEditing) => isEditing ? renderEditing() : renderReview()}
            </FormCard>

            <Modal
                open={isModalVisible}
                footer={null}
                onCancel={handleModalClose}
                width={800}
                centered
                className={s.imageModal}
            >
                <div className={s.modalContent}>
                    {currentImage && (
                        <img
                            src={currentImage}
                            alt="ID"
                            className={s.modalImage}
                        />
                    )}
                </div>
            </Modal>
        </>
    );
};

export default PersonalIndentification;